import { Link } from "wouter";
import { SEOHead, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { ALTERNATIVE_PAGES } from "@/lib/blog-data";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { ChevronRight, Rocket, Star } from "lucide-react";
import { SeoPageFooter } from "@/components/seo-page-footer";

const COMPARISONS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Builder Software Comparisons | The Proper Tool",
  "description": "See how The Proper Tool compares to other popular builder quoting and job management platforms.",
  "url": "https://thepropertool.com/comparisons",
  "publisher": {
    "@type": "Organization",
    "name": "The Proper Tool",
    "url": "https://thepropertool.com"
  }
};

const COMPARISONS_BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://thepropertool.com/"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Comparisons",
      "item": "https://thepropertool.com/comparisons"
    }
  ]
};

export default function ComparisonsIndexPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Builder Software Comparisons | The Proper Tool vs Alternatives"
        description="See how The Proper Tool compares to Tradify, Buildertrend, simPRO, Jobber and other builder software platforms for UK construction quoting."
        canonical="/comparisons"
        ogType="website"
        schema={[COMPARISONS_SCHEMA, COMPARISONS_BREADCRUMB_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="mb-12">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span>Comparisons</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Software Comparisons</h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            See how The Proper Tool compares to other popular platforms for UK builder quoting and job management.
          </p>
        </div>

        <section className="mb-10">
          <Link href="/best/quoting-software-for-uk-builders">
            <div className="group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border border-primary/30 bg-primary/5 rounded-2xl p-6 hover:border-primary/60 hover:bg-primary/10 transition-all cursor-pointer">
              <div className="flex items-start gap-4">
                <Star className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-1">Featured Guide</p>
                  <h2 className="text-lg font-bold group-hover:text-primary transition-colors">
                    Best Quoting Software for UK Builders (2026): All 8 Tools Compared
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Side-by-side comparison of eight tools: Tradify, Buildxact, simPRO, EstimatorXpress, PriceAJob, esti-mate, Spreadsheets, and The Proper Tool.
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
            </div>
          </Link>
        </section>

        <section className="mb-16">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ALTERNATIVE_PAGES.map((alt) => (
              <Link key={alt.slug} href={alt.href ?? `/alternatives/${alt.slug}`}>
                <article
                  className="group border border-border rounded-xl p-6 hover:border-primary/50 hover:shadow-md transition-all cursor-pointer flex items-center justify-between h-full"
                  data-testid={`card-comparison-${alt.slug}`}
                >
                  <div>
                    <h2 className="font-semibold text-lg group-hover:text-primary transition-colors">
                      {alt.competitor} Alternative
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">The Proper Tool vs {alt.competitor}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 ml-4" />
                </article>
              </Link>
            ))}
          </div>
        </section>

        <section className="bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
          <Rocket className="h-8 w-8 text-primary mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-3">Ready to try The Proper Tool?</h2>
          <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
            Builder quoting software built specifically for UK residential construction. Generate a professional quote from drawings or a job description in under 10 minutes.
          </p>
          <Button size="lg" asChild>
            <Link href="/login">Start free, no card needed</Link>
          </Button>
          <p className="text-xs text-muted-foreground mt-3">14-day free trial. Plans from £29/mo. No credit card required.</p>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
